import React, { useState, useEffect } from 'react';
import Card, { CardContent } from '../../components/ui/Card';
import { adminService } from '../../services/adminService';
import toast from 'react-hot-toast';

interface ApiKeyConfig {
  key: string;
  description: string;
  isConfigured: boolean;
  maskedValue: string | null;
  updatedAt: Date;
}

interface PlatformSettings {
  fees: {
    platformFeePercent: number;
    paymentProcessingFeePercent: number;
    minimumWithdrawal: number;
  };
  features: {
    allowNewCreators: boolean;
    requireKYC: boolean;
    enableComments: boolean;
    enableMessages: boolean;
    enableLivestreams: boolean;
    enableShop: boolean;
  };
  content: {
    maxPostLength: number;
    allowAdultContent: boolean;
    autoModerationEnabled: boolean;
    requireContentWarnings: boolean;
  };
  payments: {
    stripeEnabled: boolean;
    paypalEnabled: boolean;
    cryptoEnabled: boolean;
    autoPayoutEnabled: boolean;
    payoutSchedule: string;
  };
  email: {
    welcomeEmailEnabled: boolean;
    marketingEmailsEnabled: boolean;
    transactionalEmailsEnabled: boolean;
    emailProvider: string;
  };
}

const AdminSettingsPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'settings' | 'api-keys'>('settings');
  
  // API Keys state
  const [apiKeys, setApiKeys] = useState<ApiKeyConfig[]>([]);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [keyValues, setKeyValues] = useState<Record<string, string>>({});
  const [testingKey, setTestingKey] = useState<string | null>(null);
  const [revealedKeys, setRevealedKeys] = useState<Set<string>>(new Set());
  
  const [settings, setSettings] = useState<PlatformSettings>({
    fees: {
      platformFeePercent: 5,
      paymentProcessingFeePercent: 2.9,
      minimumWithdrawal: 50,
    },
    features: {
      allowNewCreators: true,
      requireKYC: false,
      enableComments: true,
      enableMessages: true,
      enableLivestreams: false,
      enableShop: false,
    },
    content: {
      maxPostLength: 10000,
      allowAdultContent: false,
      autoModerationEnabled: true,
      requireContentWarnings: false,
    },
    payments: {
      stripeEnabled: true,
      paypalEnabled: true,
      cryptoEnabled: false,
      autoPayoutEnabled: true,
      payoutSchedule: 'weekly',
    },
    email: {
      welcomeEmailEnabled: true,
      marketingEmailsEnabled: false,
      transactionalEmailsEnabled: true,
      emailProvider: 'sendgrid',
    },
  });

    // Load settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await adminService.getPlatformConfig();
        const configs = response.config || [];
        
        // Convert key-value configs to structured settings
        const loadedSettings = { ...settings };
        
        configs.forEach((config: any) => {
          const { key, value, category } = config;
          try {
            const parsedValue = JSON.parse(value);
            
            if (category === 'PAYMENT' || category === 'LEGAL' || category === 'FEATURE' || category === 'INTEGRATION') {
              // Map config keys to settings structure
              if (key.includes('fee') || key.includes('withdrawal')) {
                (loadedSettings.fees as any)[key] = parsedValue;
              } else if (key.includes('creator') || key.includes('kyc') || key.includes('comment') || key.includes('message') || key.includes('livestream') || key.includes('shop')) {
                (loadedSettings.features as any)[key] = parsedValue;
              } else if (key.includes('post') || key.includes('content') || key.includes('adult') || key.includes('moderation') || key.includes('warning')) {
                (loadedSettings.content as any)[key] = parsedValue;
              } else if (key.includes('stripe') || key.includes('paypal') || key.includes('crypto') || key.includes('payout')) {
                (loadedSettings.payments as any)[key] = parsedValue;
              } else if (key.includes('email') || key.includes('welcome') || key.includes('marketing') || key.includes('transactional') || key.includes('provider')) {
                (loadedSettings.email as any)[key] = parsedValue;
              }
            }
          } catch (e) {
            // If not JSON, treat as string/boolean
            const processedValue = value === 'true' ? true : value === 'false' ? false : isNaN(Number(value)) ? value : Number(value);
            
            if (category === 'PAYMENT' || category === 'LEGAL' || category === 'FEATURE' || category === 'INTEGRATION') {
              if (key.includes('fee') || key.includes('withdrawal')) {
                (loadedSettings.fees as any)[key] = processedValue;
              } else if (key.includes('creator') || key.includes('kyc') || key.includes('comment') || key.includes('message') || key.includes('livestream') || key.includes('shop')) {
                (loadedSettings.features as any)[key] = processedValue;
              } else if (key.includes('post') || key.includes('content') || key.includes('adult') || key.includes('moderation') || key.includes('warning')) {
                (loadedSettings.content as any)[key] = processedValue;
              } else if (key.includes('stripe') || key.includes('paypal') || key.includes('crypto') || key.includes('payout')) {
                (loadedSettings.payments as any)[key] = processedValue;
              } else if (key.includes('email') || key.includes('welcome') || key.includes('marketing') || key.includes('transactional') || key.includes('provider')) {
                (loadedSettings.email as any)[key] = processedValue;
              }
            }
          }
        });
        
        setSettings(loadedSettings);
      } catch (error) {
        console.error('Failed to load platform settings:', error);
        toast.error('Failed to load settings');
      } finally {
        setInitialLoading(false);
      }
    };

    loadSettings();
  }, []);

  // Load API keys
  useEffect(() => {
    const loadApiKeys = async () => {
      try {
        const keys = await adminService.getApiKeys();
        setApiKeys(keys);
      } catch (error) {
        console.error('Failed to load API keys:', error);
        toast.error('Failed to load API keys');
      }
    };

    if (activeTab === 'api-keys') {
      loadApiKeys();
    }
  }, [activeTab]);

  const handleSaveApiKey = async (key: string) => {
    const value = keyValues[key];
    if (!value || value.trim() === '') {
      toast.error('Please enter a valid API key');
      return;
    }

    try {
      await adminService.updateApiKey(key, value);
      toast.success('API key saved successfully');
      setEditingKey(null);
      setKeyValues({ ...keyValues, [key]: '' });
      
      // Reload API keys
      const keys = await adminService.getApiKeys();
      setApiKeys(keys);
    } catch (error) {
      console.error('Failed to save API key:', error);
      toast.error('Failed to save API key');
    }
  };

  const handleDeleteApiKey = async (key: string) => {
    if (!confirm(`Are you sure you want to clear the ${key}?`)) {
      return;
    }

    try {
      await adminService.deleteApiKey(key);
      toast.success('API key cleared');
      
      // Reload API keys
      const keys = await adminService.getApiKeys();
      setApiKeys(keys);
    } catch (error) {
      console.error('Failed to delete API key:', error);
      toast.error('Failed to delete API key');
    }
  };

  const handleTestApiKey = async (key: string) => {
    setTestingKey(key);
    try {
      const result = await adminService.testApiKey(key);
      if (result.success) {
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }
    } catch (error: any) {
      console.error('Failed to test API key:', error);
      toast.error(error.response?.data?.error || 'Failed to test API key');
    } finally {
      setTestingKey(null);
    }
  };

  const handleRevealKey = async (key: string) => {
    if (revealedKeys.has(key)) {
      // Hide the key
      setRevealedKeys(new Set([...revealedKeys].filter(k => k !== key)));
      setKeyValues({ ...keyValues, [key]: '' });
    } else {
      // Reveal the key
      if (!confirm('Are you sure you want to reveal this API key? This is sensitive data.')) {
        return;
      }
      
      try {
        const result = await adminService.decryptApiKey(key);
        setKeyValues({ ...keyValues, [key]: result.value });
        setRevealedKeys(new Set([...revealedKeys, key]));
        toast.success('API key revealed (will hide in 30 seconds)');
        
        // Auto-hide after 30 seconds
        setTimeout(() => {
          setRevealedKeys(new Set([...revealedKeys].filter(k => k !== key)));
          setKeyValues({ ...keyValues, [key]: '' });
        }, 30000);
      } catch (error) {
        console.error('Failed to reveal API key:', error);
        toast.error('Failed to reveal API key');
      }
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setSaved(false);
    try {
      // Convert structured settings to key-value pairs for API
      const configUpdates = [];
      
      // Fees
      configUpdates.push(
        adminService.updatePlatformConfig({
          key: 'platformFeePercent',
          value: JSON.stringify(settings.fees.platformFeePercent),
          category: 'PAYMENT',
          description: 'Platform fee percentage'
        })
      );
      
      configUpdates.push(
        adminService.updatePlatformConfig({
          key: 'paymentProcessingFeePercent',
          value: JSON.stringify(settings.fees.paymentProcessingFeePercent),
          category: 'PAYMENT',
          description: 'Payment processing fee percentage'
        })
      );
      
      configUpdates.push(
        adminService.updatePlatformConfig({
          key: 'minimumWithdrawal',
          value: JSON.stringify(settings.fees.minimumWithdrawal),
          category: 'PAYMENT',
          description: 'Minimum withdrawal amount'
        })
      );
      
      // Features
      Object.entries(settings.features).forEach(([key, value]) => {
        configUpdates.push(
          adminService.updatePlatformConfig({
            key,
            value: JSON.stringify(value),
            category: 'FEATURE',
            description: `${key.replace(/([A-Z])/g, ' $1').toLowerCase()}`
          })
        );
      });
      
      // Content
      Object.entries(settings.content).forEach(([key, value]) => {
        configUpdates.push(
          adminService.updatePlatformConfig({
            key,
            value: JSON.stringify(value),
            category: 'LEGAL',
            description: `${key.replace(/([A-Z])/g, ' $1').toLowerCase()}`
          })
        );
      });
      
      // Payments
      Object.entries(settings.payments).forEach(([key, value]) => {
        configUpdates.push(
          adminService.updatePlatformConfig({
            key,
            value: JSON.stringify(value),
            category: 'PAYMENT',
            description: `${key.replace(/([A-Z])/g, ' $1').toLowerCase()}`
          })
        );
      });
      
      // Email
      Object.entries(settings.email).forEach(([key, value]) => {
        configUpdates.push(
          adminService.updatePlatformConfig({
            key,
            value: JSON.stringify(value),
            category: 'INTEGRATION',
            description: `${key.replace(/([A-Z])/g, ' $1').toLowerCase()}`
          })
        );
      });
      
      // Wait for all updates to complete
      await Promise.all(configUpdates);
      
      setSaved(true);
      toast.success('Settings saved successfully');
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const updateFees = (key: keyof PlatformSettings['fees'], value: number) => {
    setSettings({
      ...settings,
      fees: { ...settings.fees, [key]: value },
    });
  };

  const updateFeature = (key: keyof PlatformSettings['features'], value: boolean) => {
    setSettings({
      ...settings,
      features: { ...settings.features, [key]: value },
    });
  };

  const updateContent = (key: keyof PlatformSettings['content'], value: boolean | number) => {
    setSettings({
      ...settings,
      content: { ...settings.content, [key]: value },
    });
  };

  const updatePayment = (key: keyof PlatformSettings['payments'], value: boolean | string) => {
    setSettings({
      ...settings,
      payments: { ...settings.payments, [key]: value },
    });
  };

  const updateEmail = (key: keyof PlatformSettings['email'], value: boolean | string) => {
    setSettings({
      ...settings,
      email: { ...settings.email, [key]: value },
    });
  };

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Platform Settings</h1>
        <p className="text-gray-600">Configure global platform settings and API keys</p>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('settings')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'settings'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Platform Settings
          </button>
          <button
            onClick={() => setActiveTab('api-keys')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'api-keys'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            API Keys
          </button>
        </nav>
      </div>

      {/* Settings Tab */}
      {activeTab === 'settings' && (
      <div className="space-y-6">
        {/* Fees & Revenue */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Fees & Revenue</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Platform Fee (%)
                </label>
                <input
                  type="number"
                  value={settings.fees.platformFeePercent}
                  onChange={(e) => updateFees('platformFeePercent', parseFloat(e.target.value))}
                  step="0.1"
                  min="0"
                  max="100"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Fee taken from creator earnings
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Processing Fee (%)
                </label>
                <input
                  type="number"
                  value={settings.fees.paymentProcessingFeePercent}
                  onChange={(e) =>
                    updateFees('paymentProcessingFeePercent', parseFloat(e.target.value))
                  }
                  step="0.1"
                  min="0"
                  max="100"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Stripe/PayPal processing fee
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Minimum Withdrawal ($)
                </label>
                <input
                  type="number"
                  value={settings.fees.minimumWithdrawal}
                  onChange={(e) => updateFees('minimumWithdrawal', parseInt(e.target.value))}
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Min amount for creator payouts
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Feature Flags */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Feature Flags</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(settings.features).map(([key, value]) => (
                <label key={key} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <span className="text-sm font-medium text-gray-700">
                    {key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())}
                  </span>
                  <button
                    type="button"
                    onClick={() => updateFeature(key as keyof PlatformSettings['features'], !value)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      value ? 'bg-orange-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        value ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </label>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Content Settings */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Content Settings</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Post Length (characters)
                </label>
                <input
                  type="number"
                  value={settings.content.maxPostLength}
                  onChange={(e) => updateContent('maxPostLength', parseInt(e.target.value))}
                  min="1000"
                  max="50000"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(['allowAdultContent', 'autoModerationEnabled', 'requireContentWarnings'] as const).map((key) => (
                  <label key={key} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <span className="text-sm font-medium text-gray-700">
                      {key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())}
                    </span>
                    <button
                      type="button"
                      onClick={() => updateContent(key, !settings.content[key])}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        settings.content[key] ? 'bg-orange-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          settings.content[key] ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </label>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payment Providers */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Payment Providers</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(['stripeEnabled', 'paypalEnabled', 'cryptoEnabled', 'autoPayoutEnabled'] as const).map((key) => (
                  <label key={key} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <span className="text-sm font-medium text-gray-700">
                      {key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())}
                    </span>
                    <button
                      type="button"
                      onClick={() => updatePayment(key, !settings.payments[key])}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        settings.payments[key] ? 'bg-orange-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          settings.payments[key] ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </label>
                ))}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payout Schedule
                </label>
                <select
                  value={settings.payments.payoutSchedule}
                  onChange={(e) => updatePayment('payoutSchedule', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Bi-weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Email Settings */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Email Settings</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Provider
                </label>
                <select
                  value={settings.email.emailProvider}
                  onChange={(e) => updateEmail('emailProvider', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="sendgrid">SendGrid</option>
                  <option value="mailgun">Mailgun</option>
                  <option value="ses">Amazon SES</option>
                  <option value="postmark">Postmark</option>
                </select>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {(['welcomeEmailEnabled', 'marketingEmailsEnabled', 'transactionalEmailsEnabled'] as const).map((key) => (
                  <label key={key} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <span className="text-sm font-medium text-gray-700">
                      {key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase()).replace('Emails', 'Emails')}
                    </span>
                    <button
                      type="button"
                      onClick={() => updateEmail(key, !settings.email[key])}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        settings.email[key] ? 'bg-orange-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          settings.email[key] ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </label>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="mt-8 flex justify-end">
          <button
            onClick={handleSave}
            disabled={loading}
            className={`px-6 py-3 rounded-lg font-medium transition-colors ${
              loading
                ? 'bg-gray-400 cursor-not-allowed'
                : saved
                ? 'bg-green-600 hover:bg-green-700'
                : 'bg-orange-600 hover:bg-orange-700'
            } text-white`}
          >
            {loading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </div>
            ) : saved ? (
              'Settings Saved ✓'
            ) : (
              'Save Settings'
            )}
          </button>
        </div>
      </div>
      )}

      {/* API Keys Tab */}
      {activeTab === 'api-keys' && (
        <div className="space-y-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <svg className="h-5 w-5 text-yellow-600 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-yellow-800">Security Warning</h3>
                <p className="mt-1 text-sm text-yellow-700">
                  API keys are sensitive credentials. Never share them publicly or commit them to version control. 
                  All keys are encrypted in the database.
                </p>
              </div>
            </div>
          </div>

          {/* Payment Providers */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <svg className="h-5 w-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                Payment Providers
              </h2>
              <div className="space-y-4">
                {apiKeys.filter(k => k.key.includes('STRIPE') || k.key.includes('PAYPAL')).map((apiKey) => (
                  <div key={apiKey.key} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h3 className="text-sm font-medium text-gray-900">{apiKey.description}</h3>
                        <p className="text-xs text-gray-500 mt-1">{apiKey.key}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        {apiKey.isConfigured && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            Configured
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {editingKey === apiKey.key ? (
                      <div className="mt-3 space-y-3">
                        <input
                          type="password"
                          value={keyValues[apiKey.key] || ''}
                          onChange={(e) => setKeyValues({ ...keyValues, [apiKey.key]: e.target.value })}
                          placeholder="Enter API key..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-mono text-sm"
                        />
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleSaveApiKey(apiKey.key)}
                            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm font-medium"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => {
                              setEditingKey(null);
                              setKeyValues({ ...keyValues, [apiKey.key]: '' });
                            }}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm font-medium"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-3 flex items-center space-x-2">
                        {apiKey.isConfigured && (
                          <>
                            <div className="flex-1 px-3 py-2 bg-gray-50 rounded-lg font-mono text-sm text-gray-600">
                              {revealedKeys.has(apiKey.key) ? keyValues[apiKey.key] : apiKey.maskedValue}
                            </div>
                            <button
                              onClick={() => handleRevealKey(apiKey.key)}
                              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                              title={revealedKeys.has(apiKey.key) ? "Hide" : "Reveal"}
                            >
                              {revealedKeys.has(apiKey.key) ? (
                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                </svg>
                              ) : (
                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              )}
                            </button>
                            <button
                              onClick={() => handleTestApiKey(apiKey.key)}
                              disabled={testingKey === apiKey.key}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg disabled:opacity-50"
                              title="Test API Key"
                            >
                              {testingKey === apiKey.key ? (
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                              ) : (
                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              )}
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => setEditingKey(apiKey.key)}
                          className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                          title="Edit"
                        >
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        {apiKey.isConfigured && (
                          <button
                            onClick={() => handleDeleteApiKey(apiKey.key)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                            title="Delete"
                          >
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Email Services */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <svg className="h-5 w-5 text-purple-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Email Services
              </h2>
              <div className="space-y-4">
                {apiKeys.filter(k => k.key.includes('SENDGRID') || k.key.includes('MAILGUN') || k.key.includes('SMTP')).map((apiKey) => (
                  <div key={apiKey.key} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h3 className="text-sm font-medium text-gray-900">{apiKey.description}</h3>
                        <p className="text-xs text-gray-500 mt-1">{apiKey.key}</p>
                      </div>
                      {apiKey.isConfigured && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          Configured
                        </span>
                      )}
                    </div>
                    
                    {editingKey === apiKey.key ? (
                      <div className="mt-3 space-y-3">
                        <input
                          type="password"
                          value={keyValues[apiKey.key] || ''}
                          onChange={(e) => setKeyValues({ ...keyValues, [apiKey.key]: e.target.value })}
                          placeholder="Enter API key..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-mono text-sm"
                        />
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleSaveApiKey(apiKey.key)}
                            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm font-medium"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => {
                              setEditingKey(null);
                              setKeyValues({ ...keyValues, [apiKey.key]: '' });
                            }}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm font-medium"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-3 flex items-center space-x-2">
                        {apiKey.isConfigured && (
                          <>
                            <div className="flex-1 px-3 py-2 bg-gray-50 rounded-lg font-mono text-sm text-gray-600">
                              {revealedKeys.has(apiKey.key) ? keyValues[apiKey.key] : apiKey.maskedValue}
                            </div>
                            <button
                              onClick={() => handleRevealKey(apiKey.key)}
                              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                              title={revealedKeys.has(apiKey.key) ? "Hide" : "Reveal"}
                            >
                              {revealedKeys.has(apiKey.key) ? (
                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                </svg>
                              ) : (
                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              )}
                            </button>
                            <button
                              onClick={() => handleTestApiKey(apiKey.key)}
                              disabled={testingKey === apiKey.key}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg disabled:opacity-50"
                              title="Test API Key"
                            >
                              {testingKey === apiKey.key ? (
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                              ) : (
                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              )}
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => setEditingKey(apiKey.key)}
                          className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                          title="Edit"
                        >
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        {apiKey.isConfigured && (
                          <button
                            onClick={() => handleDeleteApiKey(apiKey.key)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                            title="Delete"
                          >
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Third-party Services */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <svg className="h-5 w-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
                Third-party Services
              </h2>
              <div className="space-y-4">
                {apiKeys.filter(k => k.key.includes('CLOUDINARY') || k.key.includes('DISCORD') || k.key.includes('GOOGLE') || k.key.includes('FACEBOOK') || k.key.includes('GEOLOCATION') || k.key.includes('EXCHANGE')).map((apiKey) => (
                  <div key={apiKey.key} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h3 className="text-sm font-medium text-gray-900">{apiKey.description}</h3>
                        <p className="text-xs text-gray-500 mt-1">{apiKey.key}</p>
                      </div>
                      {apiKey.isConfigured && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          Configured
                        </span>
                      )}
                    </div>
                    
                    {editingKey === apiKey.key ? (
                      <div className="mt-3 space-y-3">
                        <input
                          type="password"
                          value={keyValues[apiKey.key] || ''}
                          onChange={(e) => setKeyValues({ ...keyValues, [apiKey.key]: e.target.value })}
                          placeholder="Enter API key..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-mono text-sm"
                        />
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleSaveApiKey(apiKey.key)}
                            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm font-medium"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => {
                              setEditingKey(null);
                              setKeyValues({ ...keyValues, [apiKey.key]: '' });
                            }}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm font-medium"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-3 flex items-center space-x-2">
                        {apiKey.isConfigured && (
                          <>
                            <div className="flex-1 px-3 py-2 bg-gray-50 rounded-lg font-mono text-sm text-gray-600">
                              {revealedKeys.has(apiKey.key) ? keyValues[apiKey.key] : apiKey.maskedValue}
                            </div>
                            <button
                              onClick={() => handleRevealKey(apiKey.key)}
                              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                              title={revealedKeys.has(apiKey.key) ? "Hide" : "Reveal"}
                            >
                              {revealedKeys.has(apiKey.key) ? (
                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                </svg>
                              ) : (
                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              )}
                            </button>
                            <button
                              onClick={() => handleTestApiKey(apiKey.key)}
                              disabled={testingKey === apiKey.key}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg disabled:opacity-50"
                              title="Test API Key"
                            >
                              {testingKey === apiKey.key ? (
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                              ) : (
                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              )}
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => setEditingKey(apiKey.key)}
                          className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                          title="Edit"
                        >
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        {apiKey.isConfigured && (
                          <button
                            onClick={() => handleDeleteApiKey(apiKey.key)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                            title="Delete"
                          >
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminSettingsPage;
